<script setup lang="ts">
import { computed } from 'vue';
import type { PropType } from 'vue';

import {
  type ProjectCopilotConfigurationV1Snapshot,
  ScreeningType,
  V1SnapshotThoroughness,
} from '@factoryfixinc/ats-interfaces';

import MultipleSelectionItems from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/MultipleSelectionItems/MultipleSelectionItems.vue';
import SuggestionTextArea from '@/components/Jobs/CopilotActivation/ProjectCopilotConfiguration/SuggestionTextArea/SuggestionTextArea.vue';
import PassiveCandidateScreeningCallout from '@/components/Shared/PassiveCandidateScreeningCallout.vue';
import { STICK_TO_SET_QUESTIONS_SUBTITLE } from '@/core/jobs/copilot-activation/screening-copy';
import { isEnumMember } from '@/utils/is-enum-member.util';

// Props
// ===============================
const props = defineProps({
  modelValue: {
    type: Object as PropType<ProjectCopilotConfigurationV1Snapshot>,
  },
});

// Emits
// ===============================
const emit = defineEmits<{
  (e: 'update:modelValue', payload: ProjectCopilotConfigurationV1Snapshot): void;
}>();

// Computed properties
// ===============================
const screeningTypeItems = computed(() => {
  return [
    {
      title: 'Interview and dig deeper',
      subtitle: 'Dynamic, conversational screening with follow-up questions',
      icon: 'dive-deeper' as const,
      isSelected: props.modelValue?.screeningType === ScreeningType.DYNAMIC,
      tooltipText: '',
      value: `${ScreeningType.DYNAMIC}`,
    },
    {
      title: 'Stick to set questions',
      subtitle: STICK_TO_SET_QUESTIONS_SUBTITLE,
      icon: 'checked-list-items' as const,
      isSelected: props.modelValue?.screeningType === ScreeningType.STATIC,
      showCallout: !!props.modelValue?.activeOutreachEnabled,
      tooltipText: '',
      value: `${ScreeningType.STATIC}`,
    },
    {
      title: 'No screening',
      subtitle: `Don't ask any screening questions`,
      icon: 'no-circle' as const,
      isSelected: props.modelValue?.screeningType === 'SKIP',
      tooltipText: '',
      value: 'SKIP',
    },
  ];
});

const thoroughnessItems = computed(() => {
  return [
    {
      title: 'Just check the basics',
      subtitle:
        'Verify alignment on shift, pay, and location without digging into their experience',
      icon: 'task-check' as const,
      isSelected: props.modelValue?.thoroughness === V1SnapshotThoroughness.QUICK_SCREEN,
      tooltipText: '',
      value: `${V1SnapshotThoroughness.QUICK_SCREEN}`,
    },
    {
      title: 'Screen for core qualifications',
      subtitle: 'Check the basics and dig into their experience through targeted questions',
      icon: 'list' as const,
      isSelected: props.modelValue?.thoroughness === V1SnapshotThoroughness.STANDARD,
      tooltipText: '',
      value: `${V1SnapshotThoroughness.STANDARD}`,
    },
    {
      title: 'Dive deep into their experience',
      subtitle: 'Thoroughly explore work history and skills through detailed follow-up questions',
      icon: 'magnifying-glass' as const,
      isSelected: props.modelValue?.thoroughness === V1SnapshotThoroughness.COMPREHENSIVE,
      tooltipText: '',
      value: `${V1SnapshotThoroughness.COMPREHENSIVE}`,
    },
  ];
});

const screeningType = computed({
  get: () => props.modelValue?.screeningType || ScreeningType.DYNAMIC,
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      screeningType: value,
    } as ProjectCopilotConfigurationV1Snapshot);
  },
});

function setThoroughness(value: unknown) {
  if (!isEnumMember(V1SnapshotThoroughness, value)) {
    return;
  }

  emit('update:modelValue', {
    ...props.modelValue,
    thoroughness: value,
  } as ProjectCopilotConfigurationV1Snapshot);
}

const relevantSkills = computed({
  get: () => props.modelValue?.relevantSkills || '',
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      relevantSkills: value,
    } as ProjectCopilotConfigurationV1Snapshot);
  },
});

const relevantBackground = computed({
  get: () => props.modelValue?.relevantBackground || '',
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      relevantBackground: value,
    } as ProjectCopilotConfigurationV1Snapshot);
  },
});

const immediateRejectionReason = computed({
  get: () => props.modelValue?.immediateRejectionReason || '',
  set: (value) => {
    emit('update:modelValue', {
      ...props.modelValue,
      immediateRejectionReason: value,
    } as ProjectCopilotConfigurationV1Snapshot);
  },
});

const showPassiveCandidateCallout = computed(() => !!props.modelValue?.activeOutreachEnabled);
</script>

<template>
  <section>
    <!-- Screening Type -->
    <MultipleSelectionItems
      title="How should your recruiter interview candidates for your roles?"
      title-class="!text-base !font-extrabold"
      :items="screeningTypeItems"
      selected-callout-full-width
      class="mb-8"
      @click:select="screeningType = $event"
    >
      <template #selected-callout="{ item }">
        <PassiveCandidateScreeningCallout
          v-if="item.value === `${ScreeningType.STATIC}` && showPassiveCandidateCallout"
        />
      </template>
    </MultipleSelectionItems>

    <!-- Thoroughness -->
    <MultipleSelectionItems
      title="How thorough should your recruiter be when screening?"
      title-class="!text-base !font-extrabold"
      :items="thoroughnessItems"
      class="mb-8"
      @click:select="setThoroughness"
    />

    <!-- Relevant Skills -->
    <SuggestionTextArea
      v-model="relevantSkills"
      title="What should your recruiter look for that shows a candidate is special?"
      title-class="!text-base !font-extrabold"
      :placeholder="`e.g., 5+ years of industry experience, 5+ years at a similar company`"
      optional
    />

    <!-- Relevant Background -->
    <SuggestionTextArea
      v-model="relevantBackground"
      :title="`What aspects of a candidate's background should make your recruiter willing to overlook other shortcomings?`"
      title-class="!text-base !font-extrabold"
      :placeholder="`e.g., Stable work history, experience in a similar industry, etc.`"
      optional
    />

    <!-- Immediate Rejection Reason -->
    <SuggestionTextArea
      v-model="immediateRejectionReason"
      title="What signals should tell your recruiter a candidate isn't right for this job?"
      title-class="!text-base !font-extrabold"
      :placeholder="`e.g., History of job-hopping, no industry experience, etc.`"
      optional
    />
    <p class="helper-text">
      Used to guide scoring + screening. We won't auto-reject candidates for you.
    </p>
  </section>
</template>

<style lang="scss" scoped>
.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;

  .v-combobox__selection {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }

  .v-field__outline {
    @apply text-tint-80;
  }
  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }
  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }
  }
}

.v-input :deep(.v-field.v-field--focused),
.v-input--horizontal :deep(.v-field.v-field--focused) {
  .v-field__outline {
    @apply text-highlight-500;
  }
}

.v-input :deep(.v-field.v-field--error),
.v-input--horizontal :deep(.v-field.v-field--error) {
  .v-field__outline {
    @apply text-critical-500;
  }
}

.helper-text {
  @apply text-xs font-normal text-shade-800;
}
</style>
